import { Card, Flex, Loader, SegmentedControl, Stack, Text, Title, Tooltip } from "@mantine/core";
import { IMaconomyProject } from "@schemas/projectSchema";
import { IconBriefcaseFilled, IconClock, IconSortAZ } from "@tabler/icons-react";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import McInputNew from "../../../../components/McInput/McInputNew";
import { sortItemsByRecentIds } from "../../../utils";
import { doCreateOrJoinProject } from "../../admin/restQueries";

export const ProjectTable: React.FC<{
  projects: IMaconomyProject[];
}> = ({ projects }) => {
  let history = useHistory();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const [sortByRecent, setSortByRecent] = useLocalStorage("sortByRecentProject", {
    isSortedByRecent: true,
    recentItemIds: [] as string[],
  });
  const goToProject = async (maconomy: IMaconomyProject) => {
    setLoading(true);
    const project = await doCreateOrJoinProject(maconomy);
    setLoading(false);
    if (project.useDashboardAsDefaultStartPage) {
      history.push(`/project/${project._id}/dashboard`);
    } else {
      history.push(`/project/${project._id}/apps`);
    }
  };

  const handleClick = (isSortedByRecent: boolean) => {
    setSortByRecent({ recentItemIds: sortByRecent.recentItemIds, isSortedByRecent: isSortedByRecent });
  };

  function sortItemsByRecent(items: IMaconomyProject[], recentIds: string[]) {
    if (sortByRecent.isSortedByRecent != true || searchText != "") {
      return items;
    }
    return sortItemsByRecentIds(items, recentIds, (item) => item.projectNo);
  }

  return (
    <>
      {loading === false && (
        <Stack gap="sm">
          <Flex justify="space-between" align="center" gap="xs">
            <McInputNew
              flex={1}
              placeholder="Søk..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <SegmentedControl
              data={[
                {
                  label: (
                    <Tooltip label="Sorter alfabetisk" withArrow>
                      <Flex align="center" gap={4}>
                        <IconSortAZ size={18} />
                      </Flex>
                    </Tooltip>
                  ),
                  value: "alphabetical",
                },
                {
                  label: (
                    <Tooltip label="Sorter etter nylig brukte" withArrow>
                      <Flex align="center" gap={4}>
                        <IconClock size={18} />
                      </Flex>
                    </Tooltip>
                  ),
                  value: "recent",
                },
              ]}
              value={sortByRecent.isSortedByRecent ? "recent" : "alphabetical"}
              onChange={(value) => handleClick(value === "recent")}
            />
          </Flex>
          <Stack gap="sm">
            {sortItemsByRecent(projects, sortByRecent.recentItemIds)
              .filter((project) => {
                if (searchText === "") {
                  return true;
                } else {
                  return (
                    project?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                    String(project?.projectNo)?.toLowerCase().includes(searchText.toLowerCase())
                  );
                }
              })
              .map((project) => (
                <ProjectListGroupItem key={project?.projectNo} maconomy={project} goToProject={goToProject} />
              ))}
          </Stack>
        </Stack>
      )}
      {loading && <Loader />}
    </>
  );
};

const ProjectListGroupItem: React.FC<{
  maconomy: IMaconomyProject;
  goToProject: (maconomy: IMaconomyProject) => Promise<void>;
}> = ({ maconomy, goToProject }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Card bg={hovered ? "lightgray.0" : "white"} style={{ cursor: "pointer" }} onClick={() => goToProject(maconomy)}>
      <Flex onMouseOver={() => setHovered(true)} onMouseOut={() => setHovered(false)} gap="sm" align="center" c="">
        <Text c="var(--mantine-color-dark-2)">
          <IconBriefcaseFilled size={50} />
        </Text>
        <Stack gap={0}>
          <Title order={5}>{maconomy?.name}</Title>
          <Text size="sm">{maconomy?.projectNo}</Text>
        </Stack>
      </Flex>
    </Card>
  );
};
